import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Menu } from '@bloomreach/spa-sdk';
import {
  Chip,
  DropdownComponent,
  ResponsiveService,
  SearchComponent,
} from '@frk/eds-components';
import { WINDOW } from '@ng-web-apis/common';
import { SiteConfigService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import { FiltersState, FilterType, OptionWithCount, ProductType } from '@types';
import { Logger } from '@utils/logger';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FundListingComponentConfig } from '../fund-listing-component.config';
import { PpssDownloadService } from '../services/ppss-download.service';
import { PpssService } from '../services/ppss.service';
import { PpssServiceFactory } from '../services/ppss.service.factory';
import { ProductFilterStateManagerService } from '../services/product-filter-state-manager.service';

const logger = Logger.getLogger('FundFilterComponent');

interface ProductTypeOption {
  label: string;
  value: string;
}

export type FilterChip = Chip & {
  key: string;
  trueValue?: string;
};

interface SelectedFiltersArray {
  selectedFilters: FilterChip[];
}

type DropDownMultiSelectItem = OptionWithCount & {
  checked: boolean;
};

type DropDownMultiSelect = FilterType & {
  preSelectedValues: OptionWithCount[];
};

@Component({
  selector: 'ft-fund-filters',
  templateUrl: './fund-filters.component.html',
  styleUrls: ['./fund-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundFiltersComponent implements OnInit, OnDestroy {
  @Input() componentConfiguration: FundListingComponentConfig;
  @Input() menu: Menu;
  @Input() productNumberofFilteredData: number;
  @Input() productTotalNumberOfData: number;
  @Input() isLoaded = false;
  @Input() showDownloadExcelButton = false;

  @Output() downloadExcel: EventEmitter<void> = new EventEmitter<void>();

  public state: FiltersState;
  public selectedFilterLabels: SelectedFiltersArray[] = [];
  public productTypeOptions: ProductTypeOption[] = [];
  public preSelectedValue: ProductTypeOption;
  private filterSelected: boolean; // TODO: can this be deleted?
  public filterCount: number;
  public additionalFiltersCount: number;
  public isSearchText = false;
  public isMobile$: Observable<boolean>;
  public isHandheld$: Observable<boolean>;
  private searchSubject: Subject<string> = new Subject();
  private searchSubscription: Subscription;
  public chipRemoveUrl: string;
  private ppssService: PpssService;
  private showArticle6Filter = false;
  hasResetFilters = false;

  @ViewChildren('filterSelection') filterSelection: QueryList<ElementRef>;
  @ViewChild('filterSearch') filterSearch: SearchComponent;
  @ViewChildren('filtersDropDowns')
  filtersDropDowns: QueryList<DropdownComponent>;

  constructor(
    private filterStateManagerService: ProductFilterStateManagerService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private siteConfigService: SiteConfigService,
    private translateService: TranslateService,
    private responsiveService: ResponsiveService,
    private spinner: NgxSpinnerService,
    private ppssServiceFactory: PpssServiceFactory,
    private ppssDownloadService: PpssDownloadService,
    @Inject(WINDOW) readonly windowRef: Window
  ) {
    this.isMobile$ = this.responsiveService.isMobile$();
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.ppssService = this.ppssServiceFactory.getPpssService();
  }

  public ngOnInit(): void {
    this.spinner.show('fundLoadingSpinnerFilter');

    this.showArticle6Filter = this.siteConfigService.showArticle6(
      this.ppssService.getProductType() as ProductType
    );

    if (this.menu) {
      this.mapProductTypeOptions();
    }

    // Get filter count for skeleton loader
    this.filterCount = this.siteConfigService.product?.ppss.primaryFilters[
      this.componentConfiguration?.configurationName
    ]?.length;

    this.additionalFiltersCount = this.siteConfigService.product?.ppss.additionalFilters[
      this.componentConfiguration?.configurationName
    ]?.length;

    this.filterStateManagerService.subscribe((state: FiltersState) => {
      this.state = state;
      this.mapPrimaryAndAdditionalFilterOptions();
      this.selectedFilterLabels = this.fetchSelectedFilters(this.state);

      setTimeout(() => {
        this.isLoaded = this.state?.lastAction === 'countUpdated';
        this.cdr.detectChanges();
      }, 500);

      logger.debug(this.state);
      this.updateUrl();
    });

    const urlParam = this.route?.snapshot?.queryParams?.filter;
    if (urlParam) {
      this.filterStateManagerService.mapPreFilterState(
        this.componentConfiguration,
        urlParam
      );
    } else {
      this.filterStateManagerService.mapFilterState(
        this.componentConfiguration
      );
    }

    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(500))
      .subscribe((searchedValue) => {
        this.filterStateManagerService.searchByKeyword(searchedValue);
        this.updateResetBtn(searchedValue);
      });
  }

  /**
   * Map product type options only.
   */
  private mapProductTypeOptions(): void {
    this.productTypeOptions = this.menu.siteMenuItems.map((item: any) => ({
      label: item.name,
      value: item.links.site.href,
    }));
    this.preSelectedValue = this.productTypeOptions.find((item) =>
      this.router.isActive(item.value, false)
    );
  }

  /**
   * Map primary & additional type filter options.
   */
  private mapPrimaryAndAdditionalFilterOptions(): void {
    const filters = [
      ...this.state.primaryFilters,
      ...this.state.additionalFilters,
    ];

    filters.forEach((filterType: DropDownMultiSelect) => {
      let index = 0;
      let articleIndex = null;

      if (filterType.options.length > 0) {
        filterType.options.forEach((optionProps: DropDownMultiSelectItem) => {
          if (
            filterType.label === 'SFDR' &&
            optionProps.value === '6' &&
            !this.showArticle6Filter
          ) {
            articleIndex = index;
          }
          index++;
          optionProps.label =
            filterType.label === 'SFDR'
              ? this.translateService.instant(
                  'products.filter-sfdr-label-article_' + optionProps.value
                )
              : optionProps.value;
          optionProps.checked = filterType.selected.includes(optionProps.value)
            ? true
            : false;
        });
        if (typeof articleIndex === 'number') {
          filterType.options.splice(articleIndex, 1);
        }
      }
    });

    filters.forEach((filterType: DropDownMultiSelect) => {
      filterType.preSelectedValues = filterType.options.filter(
        (optionProps: DropDownMultiSelectItem) => optionProps.checked
      );
    });
  }

  /**
   * Fetch the state and parse the selected filters.
   * Return the cumulative filter labels applied.
   * @param state FundFiltersState object
   */
  private fetchSelectedFilters(state: FiltersState): SelectedFiltersArray[] {
    const filterLabels: SelectedFiltersArray[] = [];
    let filterTypeLables: FilterChip[];

    if (state.searchKeyword.selected?.length > 0) {
      this.isSearchText = true;
    }
    const filters: FilterType[] = [
      ...state.primaryFilters,
      ...state.additionalFilters,
    ];

    filters.forEach((filter: FilterType) => {
      if (filter.selected.length > 0) {
        filterTypeLables = [];
        filter.selected.forEach((selectedValue: string) => {
          filterTypeLables.push({
            key: filter.label,
            label: this.translateService.instant(
              `products.ppss-filter-${filter.label}`
            ),
            trueValue: selectedValue,
            value:
              filter.label === 'SFDR'
                ? this.translateService.instant(
                    'products.filter-sfdr-label-article_' + selectedValue
                  )
                : selectedValue,
          });
        });
        filterLabels.push({ selectedFilters: filterTypeLables });
      }
    });

    return filterLabels;
  }

  /**
   * Handle product type selection.
   * @param event event triggered upon value selection
   */
  public selectProduct(event: any): void {
    this.router.navigate([event.value], { queryParamsHandling: 'preserve' });
    // Preserve UTM query parameters for Analytics tracking
  }

  /**
   * Handle selected filter values.
   * Send filter label and its values to filter state service to filter out the rows.
   * @param filterClass the filter class with either of the values from 'primary'/'additional'.
   * @param selection event triggered upon value selection
   */
  public updateFilter(
    filterClass: string,
    selectedLabel: string,
    selection: DropDownMultiSelectItem[]
  ): void {
    // WDE-3411: Blocking the user filter action before the fund list loads.
    // Also, let the user action of filter take place once list has been loaded.
    if (!this.isLoaded) {
      setTimeout(() => {
        this.updateFilter(filterClass, selectedLabel, selection);
      }, 1000);

      return;
    }

    this.isLoaded = false;
    const selectedValues: string[] = [];

    if (selection.length > 0) {
      selection.forEach((item) => {
        selectedValues.push(item.value);
      });
    }

    setTimeout(() => {
      this.filterStateManagerService.updateFilter(
        filterClass,
        selectedLabel,
        selectedValues
      );
    }, 0);
    this.cdr.detectChanges();
  }

  /**
   * Handle text input on filter search box.
   * @param event event triggered upon value entered
   */
  public searchByKeyword(value: string): void {
    this.isLoaded = false;
    this.searchSubject.next(value);
  }

  /**
   * Reset the selection and hence table rows.
   */
  public resetFilters(event): void {
    event?.preventDefault();

    this.isLoaded = false;
    this.filterSearch.value = '';
    this.filterSearch.hasTyped = false;
    this.isSearchText = false;
    this.cdr.detectChanges();

    // TODO this improvement is to make 'reset' click more smoooth.
    // it is currently blocked and page is not refreshed on en-lu for 2-3 seconds.
    // whole component needs to be refactored and real rxjs needs to be used.
    setTimeout(() => {
      this.filterStateManagerService.resetFilters();
      if (this.filtersDropDowns) {
        this.filtersDropDowns.forEach((dropDown) => {
          dropDown.reset();
        });
      } else {
        logger.error('filter dropdowns: ', this.filtersDropDowns);
      }
      logger.event('clicked on reset filters');
    }, 0);
  }

  /**
   * Remove the selected filter.
   * @param event event triggered upon removing the selected filter from Chip
   */
  // TODO: can this be deleted?
  private removeFilter(event: FilterChip): void {
    const key = event.key;
    let value = event.value;
    let filterClass: string;
    let filterTypeToBeRemoved: FilterType;

    /**
     * Code to remove selected SFDR filters
     */
    if (key === 'SFDR') {
      value = event.trueValue;
    }

    this.state.primaryFilters.find((filter: FilterType) => {
      if (filter.type.key === key) {
        filterTypeToBeRemoved = filter;
        filterClass = 'primary';
      }
    });
    this.state.additionalFilters.find((filter: FilterType) => {
      if (filter.type.key === key) {
        filterTypeToBeRemoved = filter;
        filterClass = 'additional';
      }
    });

    const indexOfValue = filterTypeToBeRemoved.selected.indexOf(value);

    if (this.filtersDropDowns) {
      const valueToBeremove = filterTypeToBeRemoved.selected[indexOfValue];
      this.filtersDropDowns.forEach((dropDown) => {
        if (dropDown.id === key) {
          dropDown.getSelected({
            value: valueToBeremove,
            label: value,
            checked: false,
          });
        }
      });
    } else {
      logger.error('filter dropdowns: ', this.filtersDropDowns);
    }
    logger.event('clicked on remove Chip of ', value);
  }

  private updateUrl(): void {
    const selected = this.filterStateManagerService.getSelectedFilters();
    const updateUrl = this.router
      .createUrlTree([], {
        relativeTo: this.route,
        queryParams: selected ? { filter: selected } : null,
        queryParamsHandling: 'merge',
        preserveFragment: true,
      })
      .toString();
    logger.debug('location.go()', updateUrl);
    this.location.go(updateUrl);
    this.chipRemoveUrl = this.windowRef.location.href;
  }

  private updateResetBtn(value: string): void {
    if (value.length > 0) {
      this.isSearchText = true;
    } else {
      this.isSearchText = false;
    }
  }

  public onDownloadExcel(): void {
    this.downloadExcel.emit();
  }

  ngOnDestroy(): void {
    this.filterStateManagerService.unsubscribe();
    this.searchSubscription?.unsubscribe();
  }
}
